import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon, { AntIconType } from "@/components/icons/AntIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { convertObjectToBlob } from "@/utils/formUtils";
import { useLocalSearch } from "@/utils/hooksUtils";
import { validationConstants, validationFunctions } from "@/utils/validationUtils";
import { App as AntApp, Button, Divider, Flex, Input, Popconfirm, Popover, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import debounce from "lodash/debounce";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useDeleteContractAttachment,
  useDownloadContractAttachmentsAsZip,
  useUploadContractAttachments,
} from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { useHasPermissions } from "../../../../auth/hooks";
import { formatClientName } from "../../../../client/utils";
import { ContractAttachmentType } from "../../../enums";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { Contract, ContractAttachment, CreateContractAttachment } from "../../../types";
import { contractPermissionsByContractType } from "../../../utils";
import { ContractAttachmentTypeModalForm } from "../../forms/ContractAttachmentTypeModalForm";
import { ContractClientAttachmentsModal } from "./ContractClientAttachmentsModal";

interface Props {
  contract: Contract;
}

const ATTACHMENT_TYPE_ICONS: Record<ContractAttachmentType, AntIconType> = {
  [ContractAttachmentType.CONTRACT]: "audit",
  [ContractAttachmentType.OFFER]: "file-search",
  [ContractAttachmentType.FINANCIAL_MEDIATION]: "solution",
  [ContractAttachmentType.INSURANCE_CERTIFICATE]: "safety-certificate",
  [ContractAttachmentType.TERMS_AND_CONDITIONS]: "read",
  [ContractAttachmentType.PAYMENT]: "euro",
  [ContractAttachmentType.OTHER]: "file",
};

export const ContractAttachmentsSection = ({ contract }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const contractUpdatePermission = contractPermissionsByContractType[contract.type].update;
  const hasContractUpdatePermission = useHasPermissions(contractUpdatePermission);

  const [isClientAttachmentsModalVisible, setIsClientAttachmentsModalVisible] = useState(false);
  const [attachmentTypeUpdate, setAttachmentTypeUpdate] = useState<ContractAttachment>();
  const [searchValue, setSearchValue] = useState<string>();
  const [pageIndex, setPageIndex] = useState(1);

  const uploadContractAttachments = useUploadContractAttachments();
  const deleteContractAttachment = useDeleteContractAttachment();
  const downloadContractAttachmentsAsZip = useDownloadContractAttachmentsAsZip();

  const { data: attachments, isFetching } = useLocalSearch<ContractAttachment>({
    data: contract.attachments,
    keyword: searchValue,
    getSearchedValues: (attachment) => [attachment.file.filename],
  });

  const validateSearch = (keyword: string) => {
    if (validationFunctions.validateSearchKeyword(keyword)) {
      setSearchValue(keyword);
      setPageIndex(1);
    } else {
      message.error(
        t("validation.size", {
          min: validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
          max: validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
        }),
      );
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        validateSearch(keyword);
      }, 500),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const handleAttachmentsUpload = (_: RcFile, fileList: RcFile[]): boolean => {
    if (fileList.length > 0) {
      const attachments: CreateContractAttachment[] = fileList.map((file) => ({
        filename: file.name,
        type: ContractAttachmentType.OTHER,
      }));

      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      formData.append("attachments", convertObjectToBlob(attachments));

      fileList.splice(0, fileList.length);

      uploadContractAttachments.mutate({ id: contract.id, object: formData });
    }

    return false;
  };

  const columns: ColumnsType<ContractAttachment> = [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 160,
      render: (_, record) => (record.createdAt ? formatLocaleDateTime(record.createdAt) : undefined),
    },
    {
      key: "filename",
      title: t("contract.sections.attachments.filename"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
      ),
    },
    {
      key: "size",
      title: t("contract.sections.attachments.size"),
      width: 120,
      align: "right",
      render: (_, record) => formatFileSize(record.file.size),
    },
    {
      key: "type",
      width: 50,
      align: "center",
      render: (_, record) => (
        <Popover
          styles={{ root: { textAlign: "center" } }}
          content={
            hasContractUpdatePermission && (
              <SecondaryButton
                size="small"
                icon={<AntIcon type="edit" />}
                onClick={() => setAttachmentTypeUpdate(record)}
              >
                {t("common.change")}
              </SecondaryButton>
            )
          }
          title={
            <Space>
              <span>{t("contract.enums.contractAttachmentType._label")}:</span>
              <span>{t(`contract.enums.contractAttachmentType.${record.type}`)}</span>
            </Space>
          }
        >
          <span>
            <AntIcon type={ATTACHMENT_TYPE_ICONS[record.type]} />
          </span>
        </Popover>
      ),
    },
    {
      key: "sentToClient",
      title: t("contract.sections.attachments.sentToClient"),
      width: 140,
      align: "center",
      render: (_, record) => (record.sentToClient ? <AntIcon type="check" /> : <AntIcon type="close" />),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={generatePath(CONTRACT_ROUTE_PATHS.attachment.to, { id1: contract.id, id2: record.id })}
            target="_blank"
            icon="export"
            color="blue"
            text={t("common.open")}
          />

          {record.isDeletable && (
            <>
              <Divider orientation="vertical" />

              <Popconfirm
                title={t("contract.helpers.deleteAttachmentConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => deleteContractAttachment.mutate({ id1: contract.id, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
              </Popconfirm>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="margin-top-large">
      <Divider titlePlacement="start">{t("contract.sections.attachments.label")}</Divider>

      <Flex className="margin-bottom-tiny" align="end" justify="space-between">
        <Space>
          {hasContractUpdatePermission && (
            <Button
              size="small"
              color="green"
              variant="outlined"
              icon={<AntIcon type="link" />}
              onClick={() => setIsClientAttachmentsModalVisible(true)}
            >
              {t("contract.actions.assignAttachmentsFromClient")}
            </Button>
          )}

          <Button
            size="small"
            color="primary"
            variant="outlined"
            icon={<AntIcon type="download" />}
            onClick={() => downloadContractAttachmentsAsZip.mutate(contract.id)}
            disabled={contract.attachments.length === 0}
          >
            {t("contract.actions.downloadAttachmentsAsZip")}
          </Button>
        </Space>

        <Input.Search
          style={{ width: 250 }}
          loading={isFetching}
          allowClear
          placeholder={t("common.search")}
          onSearch={validateSearch}
          onChange={(event) => handleSearchChange(event.currentTarget.value)}
        />
      </Flex>

      <Table<ContractAttachment>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: TableSizes.MEDIUM }}
        dataSource={attachments}
        pagination={{
          ...paginationTableProps,
          current: pageIndex,
          pageSize: 10,
          placement: ["bottomEnd"],
          onChange: setPageIndex,
        }}
      />

      <div className="margin-top-medium margin-bottom-small">
        <UploadDragger multiple showUploadList={false} beforeUpload={handleAttachmentsUpload} />
      </div>

      <ContractAttachmentTypeModalForm
        isOpen={!!attachmentTypeUpdate}
        contractId={contract.id}
        attachment={attachmentTypeUpdate}
        onCancel={() => setAttachmentTypeUpdate(undefined)}
      />

      <ContractClientAttachmentsModal
        isOpen={isClientAttachmentsModalVisible}
        contractId={contract.id}
        clientId={contract.clients[contract.policyHolderIndex]?.id ?? ""}
        clientName={formatClientName(contract.clients[contract.policyHolderIndex]) ?? ""}
        onCancel={() => setIsClientAttachmentsModalVisible(false)}
      />
    </div>
  );
};
